import { apiBase } from '@/config/runtime-config'

/**
 * The member voting client — deliberately NOT the shared `api` helper.
 *
 * ## A different credential
 *
 * The admin client attaches the staff session's bearer token from the session store. A member
 * holds a completely different kind of token, on a different filter chain, with no
 * authorities at all. Sharing one client would mean one interceptor deciding which of two
 * credentials to attach, and getting that wrong sends a member's token to an admin route or
 * a staff token to the ballot box. Two clients cannot make that mistake.
 *
 * ## The token lives in sessionStorage, not localStorage
 *
 * A voting session is fifteen minutes and belongs to one tab. Closing the tab should end it,
 * and a shared phone should not hand the next person a live ballot.
 */

const TOKEN_KEY = 'axis.member.token'

export const memberToken = {
  get: () => {
    try {
      return sessionStorage.getItem(TOKEN_KEY)
    } catch {
      return null      // private mode, blocked storage — treat as signed out
    }
  },
  set: (token: string) => {
    try {
      sessionStorage.setItem(TOKEN_KEY, token)
    } catch { /* nothing we can do; the session simply will not persist a reload */ }
  },
  clear: () => {
    try {
      sessionStorage.removeItem(TOKEN_KEY)
    } catch { /* ignore */ }
  },
}

export class MemberApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message)
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const token = memberToken.get()
  const response = await fetch(`${apiBase}/member${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  })
  if (response.status === 401) {
    memberToken.clear()
    throw new MemberApiError(401, 'Your voting session has ended. Sign in again.')
  }
  if (!response.ok) {
    let detail = 'Something went wrong. Please try again.'
    try {
      const body = await response.json()
      detail = body.detail ?? body.title ?? detail
    } catch { /* a non-JSON error body; keep the generic sentence */ }
    throw new MemberApiError(response.status, detail)
  }
  return response.status === 204 ? (undefined as T) : response.json() as Promise<T>
}

export interface MemberContest {
  id: string
  title: string
  seatTypeCode: string
  opensAt: string
  closesAt: string
  voted: boolean
  outstanding: boolean
  delegate: boolean
}

export interface BallotEntry {
  aspirantId: string
  ballotName: string
  ballotPosition: number | null
}

/** Always 202 and always the same shape — a registered ID looks exactly like an unknown one. */
export const authStart = (nationalId: string) =>
  call<{ maskedHint: string; expiresInSeconds: number }>('/auth/start', {
    method: 'POST', body: JSON.stringify({ nationalId }),
  })

export const authVerify = async (nationalId: string, code: string) => {
  const session = await call<{ accessToken: string; expiresIn: number }>('/auth/verify', {
    method: 'POST', body: JSON.stringify({ nationalId, code }),
  })
  memberToken.set(session.accessToken)
  return session
}

export const myContests = () => call<MemberContest[]>('/nominations')

export const myBallot = (id: string) => call<BallotEntry[]>(`/nominations/${id}/ballot`)

export const castVote = (id: string, aspirantId: string) =>
  call<{ nominationId: string; receipt: string }>(`/nominations/${id}/vote`, {
    method: 'POST', body: JSON.stringify({ aspirantId }),
  })

export const checkReceipt = (receipt: string) =>
  call<{ counted: boolean }>(`/receipts/${encodeURIComponent(receipt)}`)

export const logout = async () => {
  try {
    await call<void>('/auth/logout', { method: 'POST' })
  } finally {
    memberToken.clear()
  }
}
